import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, useData } from "@/lib/api";
import { STATUSES, STATUS_LABELS, TEMPERATURES } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_NOTE = "Interested in VA training sessions.";
const UNASSIGNED = "__none__";
const EMPTY = { first_name: "", last_name: "", address: "", email: "", phone: "", phone2: "", notes: DEFAULT_NOTE, tags: "Lead", status: "New", temperature: "", assigned_to: null };

const Field = ({ id, label, value, onChange, type = "text", required }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}{required && " *"}</Label>
    <Input id={id} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`contact-${id}-input`} />
  </div>
);

export default function ContactFormDialog({ open, onOpenChange, contact, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [associates] = useData(isAdmin ? "/admin/students" : null, []);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) setF(contact ? { ...contact, tags: (contact.tags || []).join(", "), assigned_to: contact.assigned_to || null } : EMPTY);
  }, [open, contact]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.temperature) return toast.error("Please set a Lead Status (Hot, Warm or Cold) before saving");
    setBusy(true);
    const body = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), assigned_to: f.assigned_to || null };
    try {
      const { data } = contact ? await api.put(`/contacts/${contact.id}`, body) : await api.post("/contacts", body);
      toast.success(contact ? "Contact updated" : `${data.first_name} added to Contacts`);
      onOpenChange(false);
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="contact-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{contact ? "Edit Lead" : "Add Lead"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <Field id="first_name" label="First Name" value={f.first_name} onChange={set("first_name")} required />
          <Field id="last_name" label="Last Name" value={f.last_name} onChange={set("last_name")} />
          <div className="col-span-2"><Field id="address" label="Property Address" value={f.address} onChange={set("address")} /></div>
          <div className="col-span-2"><Field id="email" label="Email" type="email" value={f.email} onChange={set("email")} /></div>
          <Field id="phone" label="Phone 1" value={f.phone} onChange={set("phone")} />
          <Field id="phone2" label="Phone 2" value={f.phone2} onChange={set("phone2")} />
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="notes">Inquiry note</Label>
            <Textarea id="notes" rows={3} value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="contact-notes-input" />
          </div>
          <Field id="tags" label="Labels (comma separated)" value={f.tags} onChange={set("tags")} />
          <div className="space-y-1.5">
            <Label>Lead temperature *</Label>
            <Select value={f.temperature || "none"} onValueChange={(v) => set("temperature")(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="contact-temp-select"><SelectValue placeholder="Required — pick one" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                {TEMPERATURES.map((t) => <SelectItem key={t} value={t} data-testid={`contact-temp-option-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Stage</Label>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="contact-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`contact-status-option-${s}`}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="col-span-2 space-y-1.5">
              <Label>Assign to Associate</Label>
              <Select value={f.assigned_to || UNASSIGNED} onValueChange={(v) => set("assigned_to")(v === UNASSIGNED ? null : v)}>
                <SelectTrigger data-testid="contact-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {associates.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`contact-assign-option-${a.id}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#3B185F] hover:bg-[#250E3F]" data-testid="contact-save-btn">{busy ? "Saving…" : "Save Lead"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
